import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';

function VMS() {
  const NUC_MODELS = [
    { id: 'nuc1', name: 'NUC12i3', vcpu: 8, type: 'nuc' },
    { id: 'nuc2', name: 'NUC12i5', vcpu: 16, type: 'nuc' },
    { id: 'nuc3', name: 'NUC12i7', vcpu: 20, type: 'nuc' },
    { id: 'nuc4', name: 'NUC12i9', vcpu: 24, type: 'nuc' },
    { id: 'nuc5', name: 'NUC13i9', vcpu: 32, type: 'nuc' }
  ];

  const SERVER_MODELS = [
    { id: 'srv1', name: '2nd Gen Intel Xeon Silver 4216 Processor', vcpu: 32, type: 'server' },
    { id: 'srv2', name: 'Intel Xeon Gold 6226R Processor', vcpu: 32, type: 'server' },
    { id: 'srv3', name: 'Intel Core i9-13900K Processor', vcpu: 32, type: 'server' },
    { id: 'srv4', name: 'Intel Xeon Gold 6230 Processor', vcpu: 40, type: 'server' },
    { id: 'srv5', name: 'Intel Xeon Silver 4316 20C40T', vcpu: 40, type: 'server' },
    { id: 'srv6', name: '13th Gen Intel Core i7 Processors', vcpu: 24, type: 'server' },
    { id: 'srv7', name: 'Intel Xeon Gold 6240R Processor', vcpu: 48, type: 'server' },
    { id: 'srv8', name: 'Intel Xeon Gold 5420 28C56T', vcpu: 56, type: 'server' },
    { id: 'srv9', name: 'Intel Xeon Gold 6433N Processor', vcpu: 64, type: 'server' },
    { id: 'srv10', name: 'Intel Xeon Platinum 8352V CPU 36 core 2.1G', vcpu: 72, type: 'server' },
    { id: 'srv11', name: 'Intel Xeon Platinum 8480 Processor', vcpu: 112, type: 'server' }
  ];

  const RESOLUTIONS = [
    { id: '720p', name: '720P (1280×720)', bitrate: 0.768 },
    { id: '4cif', name: '4CIF (704×576)', bitrate: 0.384 }
  ];

  const FPS_OPTIONS = [5, 10, 15, 20, 25, 30, 60];

  const FPS_BITRATE_MAP = {
    5:  0.384,
    10: 0.768,
    15: 1.024,
    20: 1.536,
    25: 1.792,
    30: 2.048,
    60: 4.096
  };

  const CODEC_OPTIONS = [
    { id: 'h264', name: 'H.264' },
    { id: 'h265', name: 'H.265 (HEVC)' },
    { id: 'mixed', name: 'Mixed (H.264 + H.265)' }
  ];

  // H.265 recording is 40% smaller than H.264 at the same (constant) bitrate.
  // vCPU, RAM and bandwidth are codec-independent.
  const H265_STORAGE_FACTOR = 0.6;

  const SYSTEM_VCPU_OVERHEAD = 2; // Fixed: Frontend + OS + Middleware

  const [customerName, setCustomerName] = useState('');
  const [totalCameras, setTotalCameras] = useState(1);
  const [h264Cameras, setH264Cameras] = useState(1);
  const [h265Cameras, setH265Cameras] = useState(1);
  const [resolution, setResolution] = useState('720p');
  const [fps, setFps] = useState(10);
  const [codec, setCodec] = useState('h264');
  const [customBitrate, setCustomBitrate] = useState('0.768');
  const [hoursPerDay, setHoursPerDay] = useState(24);
  const [retentionDays, setRetentionDays] = useState(7);
  const [bufferPercentage, setBufferPercentage] = useState(0);
  const [storageBufferPercentage, setStorageBufferPercentage] = useState(0);
  const [setupType, setSetupType] = useState('single');
  const [selectedModel, setSelectedModel] = useState('');
  const [slaveCount, setSlaveCount] = useState(2);
  const [slaveProcessorType, setSlaveProcessorType] = useState('single');
  const [results, setResults] = useState(null);
  const [showOutput, setShowOutput] = useState(false);
  const [showVersionInfo, setShowVersionInfo] = useState(false);

  const bitrateInputRef = useRef(null);
  const isUserTypingBitrate = useRef(false);

  // Bitrate is constant across codecs; only recording storage differs.
  const getReferenceBitrate = (resId = resolution, fpsVal = fps) => {
    const resData = RESOLUTIONS.find(r => r.id === resId);
    if (!resData) return 0.768;
    const base10fps = resData.bitrate;
    const fps10Bitrate = FPS_BITRATE_MAP[10];
    const fpsBitrate = FPS_BITRATE_MAP[fpsVal] || (FPS_BITRATE_MAP[10] * (fpsVal / 10));
    const fpsScale = fpsBitrate / fps10Bitrate;
    return parseFloat((base10fps * fpsScale).toFixed(3));
  };

  useEffect(() => {
    if (!isUserTypingBitrate.current) {
      const refBitrate = getReferenceBitrate(resolution, fps);
      setCustomBitrate(refBitrate.toFixed(3));
    }
  }, [resolution, fps]);

  const handleBitrateChange = (e) => {
    isUserTypingBitrate.current = true;
    setCustomBitrate(e.target.value);
  };

  const handleBitrateBlur = () => {
    setTimeout(() => { isUserTypingBitrate.current = false; }, 100);
  };

  const getCameraSplit = () => {
    if (codec === 'mixed') {
      const h264 = Number(h264Cameras) || 0;
      const h265 = Number(h265Cameras) || 0;
      return { h264, h265, total: h264 + h265 };
    }
    const total = Number(totalCameras) || 0;
    return codec === 'h265'
      ? { h264: 0, h265: total, total }
      : { h264: total, h265: 0, total };
  };

  const getActualBitrate = () => Number(customBitrate) || getReferenceBitrate();

  // v3.0 vCPU logic: streaming + fixed system overhead + buffer
  const calculateVCPU = () => {
    const cameras = getCameraSplit().total;
    const streamingVCPU = Math.ceil(cameras / 50);
    const systemVCPU = SYSTEM_VCPU_OVERHEAD;
    const baseVCPU = streamingVCPU + systemVCPU;
    const bufferVCPU = Math.ceil((baseVCPU * bufferPercentage) / 100);
    const totalVCPU = Math.max(4, baseVCPU + bufferVCPU);
    return { streamingVCPU, systemVCPU, baseVCPU, bufferVCPU, totalVCPU };
  };

  const calculateRAM = (vcpu) => vcpu * 2;

  const formatStorage = (gb) => {
    if (gb < 1000) return `${Math.round(gb)} GB`;
    return `${(gb / 1000).toFixed(2)} TB`;
  };

  // UPDATED: storage buffer is now driven ENTIRELY by storageBufferPercentage.
  // The old hardcoded "+20% buffered recording" step has been removed.
  // At 0% slider -> no buffer is added at all.
  const calculateStorage = () => {
    const split = getCameraSplit();
    const cameras = split.total;
    const bitrateValue = getActualBitrate();
    const days = Number(retentionDays) || 7;
    const hours = Number(hoursPerDay) || 24;

    // New formula: (Cameras × Bitrate × 3600 × Hours × Days × 1.05) / (8 × 1024)
    const overheadMultiplier = 1.05;
    const h264StorageGB = (split.h264 * bitrateValue * 3600 * hours * days * overheadMultiplier) / (8 * 1024);
    const h265StorageGB = (split.h265 * H265_STORAGE_FACTOR * bitrateValue * 3600 * hours * days * overheadMultiplier) / (8 * 1024);
    const recordingStorageGB = h264StorageGB + h265StorageGB;

    const systemStorageGB = 500;
    const baseGrandTotalGB = recordingStorageGB + systemStorageGB;
    const storageBufferGB = Math.ceil((baseGrandTotalGB * storageBufferPercentage) / 100);
    const grandTotalGB = baseGrandTotalGB + storageBufferGB;

    return {
      recordingStorageGB,
      h264StorageGB,
      h265StorageGB,
      h264Cameras: split.h264,
      h265Cameras: split.h265,
      systemStorageGB,
      baseGrandTotalGB,
      storageBufferGB,
      storageBufferPercentage,
      grandTotalGB,
      bitrate: bitrateValue,
      hours,
      days,
      cameras,
      overheadMultiplier,
      isCustomBitrate: Math.abs(Number(customBitrate) - getReferenceBitrate()) > 0.001
    };
  };

  const calculateBandwidth = () => {
    const cameras = getCameraSplit().total;
    const bitrateMbps = getActualBitrate();
    const fpsRatio = Number(fps) / 10;
    const liveBandwidthMbps = bitrateMbps * fpsRatio * cameras;
    const liveBandwidthGbps = liveBandwidthMbps / 1000;
    const peakBandwidthMbps = liveBandwidthMbps * 1.2;
    const peakBandwidthGbps = peakBandwidthMbps / 1000;
    return {
      liveMbps: liveBandwidthMbps.toFixed(1),
      liveGbps: liveBandwidthGbps.toFixed(2),
      peakMbps: peakBandwidthMbps.toFixed(1),
      peakGbps: peakBandwidthGbps.toFixed(2),
      bitrateMbps
    };
  };

  const getRecommendedHardware = (requiredVCPU) => {
    const allModels = [...NUC_MODELS, ...SERVER_MODELS].sort((a, b) => a.vcpu - b.vcpu);
    const serversSorted = [...SERVER_MODELS].sort((a, b) => a.vcpu - b.vcpu);

    const candidates = [];

    const singleMatch = allModels.find(m => m.vcpu >= requiredVCPU);
    if (singleMatch) {
      candidates.push({ model: singleMatch, setupType: 'single', effectiveVCPU: singleMatch.vcpu });
    }

    const dualMatch = serversSorted.find(m => m.vcpu * 2 >= requiredVCPU);
    if (dualMatch) {
      candidates.push({ model: dualMatch, setupType: 'dual', effectiveVCPU: dualMatch.vcpu * 2 });
    }

    if (candidates.length > 0) {
      candidates.sort((a, b) => (a.effectiveVCPU - requiredVCPU) - (b.effectiveVCPU - requiredVCPU));
      return candidates[0];
    }

    const bestServer = serversSorted[serversSorted.length - 1];
    const vcpuPerSlave = bestServer.vcpu * 2;
    const slavesNeeded = Math.ceil((requiredVCPU - 8) / vcpuPerSlave);
    return { model: bestServer, setupType: 'multi', processorType: 'dual', slavesNeeded };
  };

  const handleCalculate = () => {
    if (getCameraSplit().total < 1) {
      alert('Enter at least 1 camera.');
      return;
    }
    const vcpuCalc = calculateVCPU();
    const { streamingVCPU, systemVCPU, baseVCPU, bufferVCPU, totalVCPU } = vcpuCalc;
    const totalRAM = calculateRAM(totalVCPU);
    const storage = calculateStorage();
    const bandwidth = calculateBandwidth();
    const recommended = getRecommendedHardware(totalVCPU);

    setResults({
      streamingVCPU,
      systemVCPU,
      baseVCPU,
      bufferVCPU,
      totalVCPU,
      totalRAM,
      storage,
      bandwidth,
      totalCameras: getCameraSplit().total,
      h264Cameras: getCameraSplit().h264,
      h265Cameras: getCameraSplit().h265,
      resolution,
      fps: Number(fps),
      codec,
      customBitrate,
      hoursPerDay: Number(hoursPerDay),
      retentionDays: Number(retentionDays),
      customerName: customerName
    });

    setSetupType(recommended.setupType);
    setSelectedModel(recommended.model.id);

    if (recommended.setupType === 'multi') {
      const pt = recommended.processorType || 'dual';
      setSlaveProcessorType(pt);
      setSlaveCount(recommended.slavesNeeded || Math.ceil(totalVCPU / (recommended.model.vcpu * (pt === 'dual' ? 2 : 1))));
    }

    setShowOutput(true);
  };

  const getSelectedModelData = () => [...NUC_MODELS, ...SERVER_MODELS].find(m => m.id === selectedModel);

  const getEffectiveVCPU = (model) => {
    if (!model) return 0;
    if (setupType === 'dual') return model.vcpu * 2;
    if (setupType === 'multi') {
      const multiplier = slaveProcessorType === 'dual' ? 2 : 1;
      return 8 + (slaveCount * model.vcpu * multiplier);
    }
    return model.vcpu;
  };

  const generateOutput = () => {
    if (!results || !selectedModel) return '';
    const modelData = getSelectedModelData();
    if (!modelData) return '';
    const effectiveVCPU = getEffectiveVCPU(modelData);
    const storage = results.storage;
    const bandwidth = results.bandwidth;

    let output = '';
    if (results.customerName) {
      output += `Customer / Project: ${results.customerName}\n`;
    }
    output += `Date: ${new Date().toLocaleDateString()}\n\n`;

    output += `Actual vCPU Required: ${results.totalVCPU} vCPU\n`;
    output += `  ├ Streaming & Recording: ${results.streamingVCPU} vCPU\n`;
    output += `  ├ Frontend + OS + Middleware: ${results.systemVCPU} vCPU\n`;
    if (results.bufferVCPU > 0) output += `  └ Buffer (${bufferPercentage}%): ${results.bufferVCPU} vCPU\n`;
    output += `Actual RAM Required: ${results.totalRAM} GB\n`;
    output += `Live Bandwidth: ${bandwidth.liveMbps} Mbps (${bandwidth.liveGbps} Gbps)\n\n`;

    output += `Camera Configuration:\n`;
    const resName = RESOLUTIONS.find(r => r.id === results.resolution)?.name || results.resolution;
    const codecName = CODEC_OPTIONS.find(c => c.id === results.codec)?.name || results.codec;
    if (results.codec === 'mixed') {
      output += `  ${results.h264Cameras} cameras H.264 + ${results.h265Cameras} cameras H.265 (H.265 recording -40%)\n`;
    }
    output += `  ${results.totalCameras} streams | ${codecName} | ${resName} | ${results.fps} fps | ${Number(results.customBitrate).toFixed(3)} Mbps | ${results.retentionDays} days\n\n`;

    const storageBlock = `SSD - 500GB\nRecording Storage - ${formatStorage(storage.recordingStorageGB)}`;

    if (setupType === 'single') {
      const ram = modelData.vcpu * 2;
      const prefix = modelData.type === 'nuc' ? 'NUC BOX' : 'Dell Server (Single Processor)';
      output += `${prefix}\n${modelData.name}\n\nServer Specs:\nvCPU - ${modelData.vcpu}\nRAM - ${ram}GB\n${storageBlock}\nIOPS - 5000+\nBandwidth: ${bandwidth.peakMbps} Mbps peak`;
    } else if (setupType === 'dual') {
      const ram = modelData.vcpu * 4;
      output += `Dell Server (Dual Processor)\nDual ${modelData.name}\n\nServer Specs:\nvCPU - ${effectiveVCPU}\nRAM - ${ram}GB\n${storageBlock}\nIOPS - 5000+\nBandwidth: ${bandwidth.peakMbps} Mbps peak`;
    } else {
      const multiplier = slaveProcessorType === 'dual' ? 2 : 1;
      const vcpuPerSlave = modelData.vcpu * multiplier;
      const ramPerSlave = modelData.vcpu * 2 * multiplier;
      output += `Multi-Server Setup\nMaster: 8 vCPU, 16GB RAM\nSlaves (${slaveCount}): ${modelData.name}\nvCPU/slave: ${vcpuPerSlave}, RAM/slave: ${ramPerSlave}GB\nTotal vCPU: ${8 + slaveCount * vcpuPerSlave}\n${storageBlock}\nIOPS: 5000+\nBandwidth: ${bandwidth.peakMbps} Mbps peak`;
    }
    return output;
  };

  const generateSimpleOutput = () => {
    if (!results || !selectedModel) return '';
    const modelData = getSelectedModelData();
    const effectiveVCPU = getEffectiveVCPU(modelData);
    const effectiveRAM = calculateRAM(effectiveVCPU);
    const storage = results.storage;
    const bandwidth = results.bandwidth;

    const resName = RESOLUTIONS.find(r => r.id === results.resolution)?.name || results.resolution;
    const codecName = CODEC_OPTIONS.find(c => c.id === results.codec)?.name || results.codec;

    let output = '';
    if (results.customerName) {
      output += `Customer / Project: ${results.customerName}\n\n`;
    }

    output += `Camera Configuration:\n`;
    output += `Total Cameras: ${results.totalCameras}\n`;
    if (results.codec === 'mixed') {
      output += `  H.264 Cameras: ${results.h264Cameras}\n  H.265 Cameras: ${results.h265Cameras}\n`;
    }
    output += `Resolution: ${resName}\n`;
    output += `FPS: ${results.fps} FPS\n`;
    output += `Codec: ${codecName}\n`;
    output += `Bitrate: ${Number(results.customBitrate).toFixed(3)} Mbps\n`;
    output += `Hours per Day: ${results.hoursPerDay} hours\n`;
    output += `Retention Days: ${results.retentionDays} days\n\n`;

    output += `Actual vCPU Required: ${results.totalVCPU} vCPU\nActual RAM Required: ${results.totalRAM} GB\nLive Bandwidth: ${bandwidth.liveMbps} Mbps\n\nServer Specs:\nvCPU - ${effectiveVCPU}\nRAM - ${effectiveRAM}GB\nSSD - 500GB\nRecording Storage - ${formatStorage(storage.recordingStorageGB)}\nIOPS - 5000+\nBandwidth: ${bandwidth.peakMbps} Mbps peak`;
    return output;
  };

  const exportToExcel = () => {
    const text = generateSimpleOutput();
    const lines = text.split('\n').filter(l => l.trim());
    const ws = XLSX.utils.aoa_to_sheet(lines.map(line => [line]));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'VMS Specs');
    XLSX.writeFile(wb, 'vms_specs.xlsx');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => alert('Copied to clipboard!'));
  };

  return (
    <>
      <div className="vms-container" style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'white', padding: '16px 24px', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '0',
          textAlign: 'center', position: 'relative'
        }}>
          <h1 style={{ color: '#111827', fontSize: '28px', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
            <span style={{ color: '#111827' }}>
              DeepSight VMS Configuration Calculator
            </span>
            <span style={{ position: 'absolute', right: '24px', top: '50%', transform: 'translateY(-50%)' }}>
              <button
                type="button"
                onClick={() => setShowVersionInfo(v => !v)}
                style={{
                  fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                  border: '1px solid #2563eb', backgroundColor: '#2563eb', color: 'white',
                  cursor: 'pointer'
                }}
              >
                version-1
              </button>
              {showVersionInfo && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0,
                  marginTop: '6px', zIndex: 20, width: '340px', maxHeight: '70vh', overflowY: 'auto', textAlign: 'left',
                  backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '10px 12px',
                  fontSize: '13px', fontWeight: 'normal', color: '#374151'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>version-1 · 19-10-2026</div>
                  <div>Fixed the SSD and recording storage bug: SSD is now fixed at 500GB with recording storage shown below it.</div>
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>version-0 · 13-10-2026</div>
                    <div>Added the H265, H265+H264 codec, which results in a 40% reduction of recording storage.</div>
                    <div style={{ marginTop: '6px', fontSize: '12px' }}>
                      Commit:{' '}
                      <a
                        href="https://github.com/DeepSightAILabs/Calculators/commit/69535582a59ea450d156407138a7bb7a4fe75ea0"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb', fontFamily: 'monospace' }}
                      >
                        6953558
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </span>
          </h1>
        </div>

        {/* Customer Info Section */}
        <div className="vms-customer-info">
          <div className="vms-section-header">Customer Info</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label className="vms-customer-label">Customer / Project name — optional, used in PDF printout</label>
            <input 
              type="text" 
              className="vms-customer-input" 
              placeholder="Customer / project name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>
        </div>

        {/* General Settings header */}
        <div className="vms-section-header">General Settings</div>

        {/* Camera Group Card (Matching Second Image layout with sidebar) */}
        <div className="vms-groups-list">
          <div className="vms-group-card">
            {/* Left Column (Group sidebar) */}
            <div className="vms-group-sidebar">
              <span className="vms-group-title">Camera count</span>
              {codec === 'mixed' ? (
                <div style={{ width: '100%' }}>
                  <label className="vms-group-streams-label">H.264 cameras</label>
                  <input 
                    type="number" 
                    min="0" 
                    className="vms-group-streams-input" 
                    value={h264Cameras}
                    onChange={(e) => setH264Cameras(Number(e.target.value) || 0)}
                  />
                  <label className="vms-group-streams-label" style={{ marginTop: '8px' }}>H.265 cameras</label>
                  <input 
                    type="number" 
                    min="0" 
                    className="vms-group-streams-input" 
                    value={h265Cameras}
                    onChange={(e) => setH265Cameras(Number(e.target.value) || 0)}
                  />
                  <div style={{ fontSize: '12px', marginTop: '6px' }}>Total: {getCameraSplit().total}</div>
                </div>
              ) : (
                <div style={{ width: '100%' }}>
                  <label className="vms-group-streams-label">Number of cameras</label>
                  <input 
                    type="number" 
                    min="1" 
                    className="vms-group-streams-input" 
                    value={totalCameras}
                    onChange={(e) => setTotalCameras(Number(e.target.value) || 0)}
                  />
                </div>
              )}
            </div>

            {/* Right Column (Inputs Grid) */}
            <div className="vms-group-main">
              <div className="vms-group-subsection-title">
                Main streams <span className="vms-info-icon" title="Main stream settings for recording and live preview">i</span>
              </div>
              
              <div className="vms-field-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="vms-field-item">
                  <label className="vms-field-label">Compression</label>
                  <select 
                    className="vms-field-select" 
                    value={codec} 
                    onChange={(e) => setCodec(e.target.value)}
                  >
                    {CODEC_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                
                <div className="vms-field-item">
                  <label className="vms-field-label">Resolution</label>
                  <select 
                    className="vms-field-select" 
                    value={resolution} 
                    onChange={(e) => setResolution(e.target.value)}
                  >
                    {RESOLUTIONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                
                <div className="vms-field-item">
                  <label className="vms-field-label">FPS</label>
                  <select 
                    className="vms-field-select" 
                    value={fps} 
                    onChange={(e) => setFps(Number(e.target.value))}
                  >
                    {FPS_OPTIONS.map(f => <option key={f} value={f}>{f} fps</option>)}
                  </select>
                </div>
                
                <div className="vms-field-item">
                  <label className="vms-field-label">
                    Bitrate (Mbps) <span className="vms-info-icon" title={`Reference: ${getReferenceBitrate()} Mbps`}>i</span>
                  </label>
                  <input 
                    ref={bitrateInputRef}
                    type="number" 
                    min="0.001" 
                    step="0.001"
                    className="vms-field-input" 
                    value={customBitrate} 
                    onChange={handleBitrateChange}
                    onBlur={handleBitrateBlur}
                  />
                </div>

                <div className="vms-field-item">
                  <label className="vms-field-label">Hours per Day (Recording)</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="24"
                    className="vms-field-input" 
                    value={hoursPerDay}
                    onChange={(e) => setHoursPerDay(Number(e.target.value) || 0)}
                  />
                </div>

                <div className="vms-field-item">
                  <label className="vms-field-label">Days to store</label>
                  <input 
                    type="number" 
                    min="1" 
                    className="vms-field-input" 
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(Number(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global Settings Sliders (matching separate cards below Camera count) */}
        <div className="vms-global-settings-grid" style={{ marginTop: '10px' }}>
          <div className="vms-global-settings-card">
            <div className="vms-slider-container">
              <div className="vms-slider-header">
                <span>Buffer (vCPU & RAM)</span>
                <span>{bufferPercentage}%</span>
              </div>
              <input 
                type="range" 
                className="vms-slider" 
                min="0" 
                max="50" 
                value={bufferPercentage} 
                onChange={(e) => setBufferPercentage(Number(e.target.value))}
              />
            </div>
          </div>
          
          <div className="vms-global-settings-card">
            <div className="vms-slider-container">
              <div className="vms-slider-header">
                <span>Storage Buffer</span>
                <span>{storageBufferPercentage}%</span>
              </div>
              <span className="vms-slider-desc">Added on top of Grand Total Storage</span>
              <input 
                type="range" 
                className="vms-slider" 
                min="0" 
                max="100" 
                value={storageBufferPercentage} 
                onChange={(e) => setStorageBufferPercentage(Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        <button className="vms-calculate-btn" onClick={handleCalculate}>
          Calculate Server Specs + Bandwidth
        </button>

        {/* Output Section */}
        {results && showOutput && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '30px', backgroundColor: 'white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
            <h2 style={{ color: '#111827', textAlign: 'center', marginBottom: '30px', fontSize: '30px' }}>
              DeepSight VMS Server Configuration
            </h2>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginBottom: '30px' }}>
              <div style={{ background: '#1a6fdb', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(26,111,219,0.2)' }}>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>Cameras</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{results.totalCameras}</div>
              </div>
              <div style={{ background: '#1a6fdb', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(26,111,219,0.2)' }}>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>vCPU Required</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{results.totalVCPU}</div>
              </div>
              <div style={{ background: '#1a6fdb', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(26,111,219,0.2)' }}>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>RAM Required</div>
                <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{results.totalRAM}GB</div>
              </div>
              <div style={{ background: '#1a6fdb', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(26,111,219,0.2)' }}>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>Recording Storage</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{formatStorage(results.storage.recordingStorageGB)}</div>
              </div>
              <div style={{ background: '#1a6fdb', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(26,111,219,0.2)' }}>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>Total Storage</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{formatStorage(results.storage.grandTotalGB)}</div>
              </div>
              <div style={{ background: '#1a6fdb', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(26,111,219,0.2)' }}>
                <div style={{ fontSize: '13px', marginBottom: '8px' }}>Live Bandwidth</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{results.bandwidth.liveGbps} Gbps</div>
              </div>
            </div>

            {/* Setup Type & Model Selection */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '20px' }}>
              <div style={{ backgroundColor: '#f0f7f8', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#1e293b', fontSize: '15px' }}>Setup Type</label>
                <select value={setupType} onChange={(e) => setSetupType(e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '15px', backgroundColor: 'white' }}>
                  <option value="single">Single Server</option>
                  <option value="dual">Dual Server</option>
                  <option value="multi">Multi-Server</option>
                </select>
              </div>
              <div style={{ backgroundColor: '#f0f7f8', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#1e293b', fontSize: '15px' }}>Server Model</label>
                <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '15px', backgroundColor: 'white' }}>
                  <option value="">Select Server</option>
                  {[...NUC_MODELS, ...SERVER_MODELS].sort((a, b) => a.vcpu - b.vcpu).map(model => {
                    const vcpu = setupType === 'dual' ? model.vcpu * 2 : model.vcpu;
                    const ram = setupType === 'dual' ? model.vcpu * 4 : model.vcpu * 2;
                    return <option key={model.id} value={model.id}>{model.name} ({vcpu}vCPU, {ram}GB)</option>;
                  })}
                </select>
              </div>
            </div>

            {/* Config Output Panels */}
            {selectedModel && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <div style={{ background: '#1a6fdb', padding: '15px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>Detailed Config</h3>
                    <button onClick={() => copyToClipboard(generateOutput())} style={{ padding: '8px 16px', background: 'white', color: '#111827', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Copy</button>
                  </div>
                  <pre style={{ padding: '25px', margin: 0, background: '#ffffff', fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-wrap', maxHeight: '400px', overflowY: 'auto' }}>
                    {generateOutput()}
                  </pre>
                </div>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <div style={{ background: '#1a6fdb', padding: '15px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>Summary Specs</h3>
                    <div>
                      <button onClick={() => copyToClipboard(generateSimpleOutput())} style={{ padding: '6px 12px', background: 'white', color: '#111827', border: 'none', borderRadius: '4px', marginRight: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Copy</button>
                      <button onClick={exportToExcel} style={{ padding: '6px 12px', background: 'white', color: '#111827', border: 'none', borderRadius: '4px', marginRight: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Excel</button>
                      <button onClick={() => window.print()} style={{ padding: '6px 12px', background: 'white', color: '#111827', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Export PDF</button>
                    </div>
                  </div>
                  <pre style={{ padding: '25px', margin: 0, background: '#ffffff', fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                    {generateSimpleOutput()}
                  </pre>
                </div>
              </div>
            )}

            {/* Centered Export Action Buttons (Placed Below the panels) */}
            {selectedModel && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', margin: '24px 0' }}>
                <button 
                  onClick={() => window.print()} 
                  style={{ padding: '12px 28px', backgroundColor: '#1a6fdb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(26,111,219,0.25)', transition: 'background-color 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1558b0'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a6fdb'}
                >
                  Export PDF Report
                </button>
                <button 
                  onClick={exportToExcel} 
                  style={{ padding: '12px 28px', backgroundColor: 'white', color: '#111827', border: '2px solid #1a6fdb', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background-color 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f0fc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  Export Excel Specs
                </button>
              </div>
            )}

            {/* Industry Standard Formula Section */}
            <div style={{ padding: '30px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1.5px solid #b8d0f5', marginTop: '20px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#111827', fontWeight: 'bold', textAlign: 'center' }}>
                Industry Standard Storage Formula
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', fontSize: '14px', lineHeight: '1.8', color: '#333' }}>
                {/* Left Column */}
                <div>
                  <div style={{ fontWeight: 'bold', color: '#111827', marginBottom: '10px', fontSize: '15px' }}>Recording Storage Formula:</div>
                  <div style={{ background: '#f0f6ff', padding: '15px', borderRadius: '8px', border: '1px solid #b8d0f5', marginBottom: '15px' }}>
                    <strong>Recording Storage (GB) = (Cameras × CodecFactor × Bitrate × 3600 × Hours × Days × 1.05) ÷ (8 × 1024)</strong>
                  </div>
                  <div><strong>Where:</strong></div>
                  <div>• Cameras = total number of cameras</div>
                  <div>• CodecFactor = 1.0 for H.264, 0.6 for H.265 (40% less recording)</div>
                  <div>• Bitrate = in Mbps (same for both codecs; from table or custom)</div>
                  <div>• 3600 = seconds per hour</div>
                  <div>• Hours = recording hours per day</div>
                  <div>• Days = retention period</div>
                  <div>• 1.05 = 5% overhead factor</div>
                  <div>• ÷ (8 × 1024) = convert Mbps → GB</div>

                  <div style={{ fontWeight: 'bold', color: '#111827', margin: '18px 0 10px 0', fontSize: '15px' }}>Bitrate Reference Table (at 10fps, same for H.264 &amp; H.265):</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#1a6fdb', color: 'white' }}>
                        <th style={{ padding: '8px', border: '1px solid #ddd' }}>Resolution</th>
                        <th style={{ padding: '8px', border: '1px solid #ddd' }}>Bitrate</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: '#f0f6ff' }}>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>720P (1280×720)</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}><strong>0.768 Mbps</strong></td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>4CIF (704×576)</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}><strong>0.384 Mbps</strong></td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ fontWeight: 'bold', color: '#111827', margin: '18px 0 10px 0', fontSize: '15px' }}>FPS → Bitrate Reference (720p, both codecs):</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#1a6fdb', color: 'white' }}>
                        <th style={{ padding: '8px', border: '1px solid #ddd' }}>FPS</th>
                        <th style={{ padding: '8px', border: '1px solid #ddd' }}>Bitrate (Mbps)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[5, 10, 15, 20, 25, 30, 60].map((f) => (
                        <tr key={f} style={{ background: f === results.fps ? '#dbeafe' : 'white' }}>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: f === results.fps ? 'bold' : 'normal' }}>
                            {f} FPS {f === results.fps ? '← selected' : ''}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: f === results.fps ? 'bold' : 'normal', color: f === results.fps ? '#111827' : 'inherit' }}>
                            {FPS_BITRATE_MAP[f]} Mbps
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{ fontWeight: 'bold', color: '#111827', marginBottom: '10px', fontSize: '15px' }}>Your Calculation:</div>
                  <div style={{ background: '#dbeafe', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                    <div><strong>Resolution:</strong> {RESOLUTIONS.find(r => r.id === results.resolution)?.name}</div>
                    <div><strong>Codec:</strong> {CODEC_OPTIONS.find(c => c.id === results.codec)?.name}</div>
                    {results.codec === 'mixed' && (
                      <div><strong>Cameras:</strong> {results.h264Cameras} × H.264 + {results.h265Cameras} × H.265</div>
                    )}
                    <div><strong>FPS:</strong> {results.fps} FPS</div>
                    <div><strong>Bitrate:</strong> <span style={{ color: '#111827', fontWeight: 'bold' }}>
                      {results.storage.bitrate.toFixed(3)} Mbps {results.storage.isCustomBitrate && '(Custom)'}
                    </span></div>
                    <div><strong>Live Bandwidth:</strong> <span style={{ color: '#111827', fontWeight: 'bold', fontSize: '18px' }}>{results.bandwidth.liveMbps} Mbps ({results.bandwidth.liveGbps} Gbps)</span></div>
                    <div><strong>Recording Storage:</strong> <span style={{ color: '#111827', fontWeight: 'bold', fontSize: '18px' }}>{formatStorage(results.storage.recordingStorageGB)}</span></div>
                  </div>

                  <div style={{ fontWeight: 'bold', color: '#111827', marginBottom: '10px', fontSize: '15px' }}>Bandwidth Formula:</div>
                  <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '15px' }}>
                    <div style={{ color: '#111827' }}><strong>Live Bandwidth = Bitrate × (FPS/10) × Cameras</strong></div>
                    <div style={{ marginTop: '8px', color: '#111827', fontSize: '13px', fontFamily: 'monospace' }}>
                      {results.storage.bitrate.toFixed(3)} × {(results.fps / 10).toFixed(1)} × {results.totalCameras} ={' '}
                      <strong>{results.bandwidth.liveMbps} Mbps ({results.bandwidth.liveGbps} Gbps)</strong>
                    </div>
                    <div style={{ marginTop: '8px', color: '#111827', fontSize: '12px' }}>
                      Peak: <strong>{results.bandwidth.peakMbps} Mbps</strong> (+20% overhead)
                    </div>
                  </div>

                  {/* UPDATED Storage Breakdown: hardcoded +20% "Buffered Recording" step removed.
                      Steps renumbered. Buffer step now only appears (and only affects the total)
                      when storageBufferPercentage > 0. */}
                  <div style={{ fontWeight: 'bold', color: '#111827', marginBottom: '10px', fontSize: '15px' }}>Storage Breakdown:</div>
                  <div style={{ background: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '15px' }}>
                    <div style={{ marginBottom: '8px', color: '#111827' }}><strong>1. Recording Storage:</strong></div>
                    <div style={{ marginLeft: '15px', color: '#111827', fontSize: '13px', fontFamily: 'monospace' }}>
                      {results.storage.h264Cameras > 0 && (
                        <div>
                          H.264: ({results.storage.h264Cameras} × {results.storage.bitrate.toFixed(3)} × 3600 × {results.storage.hours} × {results.storage.days} × {results.storage.overheadMultiplier}) ÷ (8 × 1024) ={' '}
                          <strong>{formatStorage(results.storage.h264StorageGB)}</strong>
                        </div>
                      )}
                      {results.storage.h265Cameras > 0 && (
                        <div>
                          H.265: ({results.storage.h265Cameras} × {H265_STORAGE_FACTOR} × {results.storage.bitrate.toFixed(3)} × 3600 × {results.storage.hours} × {results.storage.days} × {results.storage.overheadMultiplier}) ÷ (8 × 1024) ={' '}
                          <strong>{formatStorage(results.storage.h265StorageGB)}</strong>
                        </div>
                      )}
                      Total recording = <strong>{formatStorage(results.storage.recordingStorageGB)}</strong>
                    </div>
                    <div style={{ margin: '12px 0 8px 0', color: '#111827' }}><strong>2. OS / System Storage:</strong></div>
                    <div style={{ marginLeft: '15px', color: '#111827', fontSize: '13px', fontFamily: 'monospace' }}>
                      <strong>500 GB</strong>
                    </div>
                    <div style={{ margin: '12px 0 8px 0', borderTop: '1px solid #ddd', paddingTop: '10px', color: '#111827' }}>
                      <strong>3. Base Grand Total (before storage buffer):</strong>
                    </div>
                    <div style={{ marginLeft: '15px', color: '#111827', fontSize: '13px', fontFamily: 'monospace' }}>
                      {formatStorage(results.storage.recordingStorageGB)} + 500 GB = <strong>{formatStorage(results.storage.baseGrandTotalGB)}</strong>
                    </div>
                    {results.storage.storageBufferPercentage > 0 && (
                      <>
                        <div style={{ margin: '12px 0 8px 0', color: '#111827' }}><strong>4. Storage Buffer (+{results.storage.storageBufferPercentage}%):</strong></div>
                        <div style={{ marginLeft: '15px', color: '#111827', fontSize: '13px', fontFamily: 'monospace' }}>
                          {formatStorage(results.storage.baseGrandTotalGB)} × {results.storage.storageBufferPercentage}% = +<strong>{formatStorage(results.storage.storageBufferGB)}</strong>
                        </div>
                      </>
                    )}
                    <div style={{ margin: '12px 0 8px 0', borderTop: '2px solid #1a6fdb', paddingTop: '10px', color: '#111827' }}>
                      <strong>{results.storage.storageBufferPercentage > 0 ? '5.' : '4.'} Grand Total Storage:</strong>
                    </div>
                    <div style={{ marginLeft: '15px', color: '#111827', fontSize: '13px', fontFamily: 'monospace' }}>
                      {results.storage.storageBufferPercentage > 0
                        ? <>{formatStorage(results.storage.baseGrandTotalGB)} + {formatStorage(results.storage.storageBufferGB)} = <strong style={{ fontSize: '15px' }}>{formatStorage(results.storage.grandTotalGB)}</strong></>
                        : <>{formatStorage(results.storage.recordingStorageGB)} + 500 GB = <strong style={{ fontSize: '15px' }}>{formatStorage(results.storage.grandTotalGB)}</strong></>
                      }
                    </div>
                  </div>

                  {/* vCPU Breakdown Table */}
                  <div style={{ fontWeight: 'bold', color: '#111827', marginBottom: '10px', fontSize: '15px' }}>vCPU Calculation:</div>
                  <div style={{ background: '#f0f8ff', padding: '15px', borderRadius: '8px', border: '1px solid #aed6f1', fontSize: '13px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#2563eb', color: 'white' }}>
                          <th style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'left' }}>Component</th>
                          <th style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center' }}>Rule</th>
                          <th style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center' }}>vCPU</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ background: 'white' }}>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1' }}>Streaming &amp; Recording</td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontFamily: 'monospace', color: '#111827' }}>
                            ceil({results.totalCameras} ÷ 50) = {results.streamingVCPU}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontWeight: 'bold' }}>{results.streamingVCPU}</td>
                        </tr>
                        <tr style={{ background: '#ffffff' }}>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1' }}>Frontend + OS + Middleware</td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontFamily: 'monospace', color: '#111827' }}>Fixed overhead</td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontWeight: 'bold' }}>{results.systemVCPU}</td>
                        </tr>
                        <tr style={{ background: 'white' }}>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', fontWeight: 'bold' }}>Base vCPU</td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontFamily: 'monospace', color: '#111827' }}>
                            {results.streamingVCPU} + {results.systemVCPU}
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontWeight: 'bold' }}>{results.baseVCPU}</td>
                        </tr>
                        {results.bufferVCPU > 0 && (
                          <tr style={{ background: '#ffffff' }}>
                            <td style={{ padding: '8px', border: '1px solid #aed6f1' }}>Buffer ({bufferPercentage}%)</td>
                            <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontFamily: 'monospace', color: '#111827' }}>
                              ceil({results.baseVCPU} × {bufferPercentage / 100})
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #aed6f1', textAlign: 'center', fontWeight: 'bold' }}>{results.bufferVCPU}</td>
                          </tr>
                        )}
                        <tr style={{ background: '#2563eb' }}>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', color: 'white', fontWeight: 'bold' }}>Total vCPU</td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', color: 'white', textAlign: 'center', fontFamily: 'monospace' }}>
                            max(4, {results.baseVCPU} + {results.bufferVCPU})
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #aed6f1', color: 'white', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>{results.totalVCPU}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                      Min 4 vCPU enforced • RAM = vCPU × 2 GB
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden PDF Printable Report - Rendered outside of .vms-container */}
      {results && (
        <div className="vms-print-report" style={{ padding: '20px', fontFamily: 'sans-serif', color: '#1e293b' }}>
          <div style={{ borderBottom: '2px solid #1a6fdb', paddingBottom: '15px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ color: '#111827', margin: 0, fontSize: '24px' }}>DeepSight VMS Specification Report</h1>
            <span style={{ fontSize: '12px', color: '#64748b' }}>Date: {new Date().toLocaleDateString()}</span>
          </div>

          {results.customerName && (
            <div style={{ marginBottom: '20px', background: '#f8fafc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <strong style={{ color: '#111827' }}>Customer / Project:</strong> {results.customerName}
            </div>
          )}

          <h2 style={{ fontSize: '18px', color: '#111827', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>Server Specifications</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '25px', fontSize: '14px' }}>
            <div><strong>vCPU Required:</strong> {results.totalVCPU} vCPU</div>
            <div><strong>RAM Required:</strong> {results.totalRAM} GB</div>
            <div><strong>Total Storage:</strong> {formatStorage(results.storage.grandTotalGB)}</div>
            <div><strong>Live Bandwidth:</strong> {results.bandwidth.liveMbps} Mbps ({results.bandwidth.liveGbps} Gbps)</div>
            <div><strong>Recommended Setup:</strong> {setupType === 'single' ? 'Single Server' : setupType === 'dual' ? 'Dual Server' : 'Multi-Server Setup'}</div>
            {results.storage.recordingStorageGB && (
              <div><strong>Recording Storage:</strong> {formatStorage(results.storage.recordingStorageGB)}</div>
            )}
          </div>

          <h2 style={{ fontSize: '18px', color: '#111827', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>Camera Configuration</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Total Cameras</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Compression</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Resolution</th>
                <th style={{ padding: '8px', textAlign: 'center' }}>FPS</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Bitrate</th>
                <th style={{ padding: '8px', textAlign: 'center' }}>Hours per Day</th>
                <th style={{ padding: '8px', textAlign: 'center' }}>Retention</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '8px' }}><strong>{results.totalCameras}</strong></td>
                <td style={{ padding: '8px' }}>
                  {CODEC_OPTIONS.find(c => c.id === results.codec)?.name || results.codec}
                  {results.codec === 'mixed' && ` (${results.h264Cameras} H.264 / ${results.h265Cameras} H.265)`}
                </td>
                <td style={{ padding: '8px' }}>{RESOLUTIONS.find(r => r.id === results.resolution)?.name || results.resolution}</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{results.fps} FPS</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{Number(results.customBitrate).toFixed(3)} Mbps</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{results.hoursPerDay} hours</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>{results.retentionDays} days</td>
              </tr>
            </tbody>
          </table>

          <h2 style={{ fontSize: '18px', color: '#111827', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>Detailed Specification Output</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '11px', background: '#fafbfc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            {generateOutput()}
          </pre>
        </div>
      )}
    </>
  );
}

export default VMS;
